import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Banknote, CreditCard, ShoppingBag, X } from "lucide-react";

import AddressSummary from "../pages/AddressSummary";
import RegisterModal from "../modals/RegisterModal";
import CartDrawerItem from "./CartDrawerItem";

import { useCartLogic } from "../../logic/useCartLogic";
import { useCheckout } from "../../logic/useCheckout";
import { calculateCartTotals, PAYMENT_MODE } from "../../utils/cartUtils";
import {
  BuyNowIntent,
  clearBuyNowIntent,
  getBuyNowIntent,
} from "../../utilities/buyNowIntent";
import { sanitizeForUrl } from "../../utils/urlSanitizer";
import type { RootState } from "../../store/store";
import { closeCartDrawer } from "../../store/uiSlice";

import "../../customCSS/CartDrawer.css";

type CartItemWithMapping = {
  sellerPriceDTO?: { mappingCode?: unknown };
  mappingCode?: unknown;
  variationCode?: unknown;
};

const getCartItemMappingCode = (item: CartItemWithMapping): string =>
  String(item.sellerPriceDTO?.mappingCode ?? item.mappingCode ?? item.variationCode ?? "");

/**
 * The cart — a right-hand slide-over that is also the whole checkout
 * (items, delivery address, payment method and place-order all live here).
 * There is no cart page: /checkout/cart just opens this (see pages/Cart.tsx).
 *
 * Mounted once in App.tsx. The contents only mount while it's open, so the
 * bag is fetched fresh on every open and never on ordinary page loads.
 */
const CartDrawer: React.FC = () => {
  const open = useSelector((state: RootState) => state.ui.cartDrawerOpen);
  return open ? <CartDrawerContent /> : null;
};

/**
 * Layout is three fixed parts — header, scrollable middle, pinned footer —
 * so the total and the place-order button are always visible instead of
 * being buried at the bottom of a long scroll.
 */
const CartDrawerContent: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleClose = useCallback(() => dispatch(closeCartDrawer()), [dispatch]);

  const {
    cartItems,
    selectedIds,
    setSelectedIds,
    loading,
    pendingQtyUpdates,
    isGuest,
    enableMode,
    handleQuantityChange,
    proceed,
    fetchCartItems,
  } = useCartLogic() as any;

  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [paymentMode, setPaymentMode] = useState<number>(PAYMENT_MODE.ONLINE);
  const [isProcessing, setIsProcessing] = useState(false);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [pendingBuyNowIntent, setPendingBuyNowIntent] =
    useState<BuyNowIntent | null>(null);

  // Close on Escape, and lock the page behind the drawer while it's open.
  // The lock is a class on <html> (see CartDrawer.css) rather than an inline
  // body style, because other components write document.body.style.overflow
  // directly and would otherwise clobber it.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.documentElement.classList.add("cd-scroll-locked");
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.documentElement.classList.remove("cd-scroll-locked");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [handleClose]);

  /* ----------------------------- derived ----------------------------- */

  const selectedProducts = useMemo(
    () =>
      cartItems.filter((product: CartItemWithMapping) =>
        selectedIds.includes(getCartItemMappingCode(product)),
      ),
    [cartItems, selectedIds],
  );

  const { totalMRP, totalDiscount, shippingFee, totalAmount, hasOutOfStock } = useMemo(
    () => calculateCartTotals(selectedProducts),
    [selectedProducts],
  );

  const onlineAvailable = enableMode?.online ?? true;
  // Cash on delivery is for registered accounts only.
  const cashAvailable = (enableMode?.cash ?? true) && !isGuest;
  const canPay = onlineAvailable || cashAvailable;

  // Keep the chosen mode on something that's actually available.
  const effectivePaymentMode =
    paymentMode === PAYMENT_MODE.ONLINE && !onlineAvailable
      ? PAYMENT_MODE.CASH
      : paymentMode === PAYMENT_MODE.CASH && !cashAvailable
        ? PAYMENT_MODE.ONLINE
        : paymentMode;

  const allSelected = cartItems.length > 0 && selectedIds.length === cartItems.length;
  const canPlaceOrder = selectedProducts.length > 0 && !hasOutOfStock;
  const readyToPay = canPlaceOrder && !!selectedAddressId && canPay;

  /** The single next thing blocking checkout, most-blocking first. */
  const blockingReason = !cartItems.length
    ? null
    : selectedProducts.length === 0
      ? "Select at least one item to continue."
      : hasOutOfStock
        ? "Remove the out-of-stock items to continue."
        : !selectedAddressId
          ? "Add a delivery address to continue."
          : !canPay
            ? "No payment method is available right now."
            : null;

  /* ----------------------------- handlers ----------------------------- */

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedIds(
        checked ? cartItems.map(getCartItemMappingCode).filter(Boolean) : [],
      );
    },
    [cartItems, setSelectedIds],
  );

  const handleSelectItem = useCallback(
    (id: string, selected: boolean) => {
      setSelectedIds((prev: string[]) =>
        selected ? [...prev, id] : prev.filter((x) => x !== id),
      );
    },
    [setSelectedIds],
  );

  const handleItemRemoved = useCallback(async () => {
    await fetchCartItems(true);
  }, [fetchCartItems]);

  const openProduct = useCallback(
    (product: any) => {
      const mappingCode = getCartItemMappingCode(product);
      handleClose();
      if (!mappingCode) return;
      navigate(
        `/product/${sanitizeForUrl(product.product)}/${sanitizeForUrl(mappingCode)}`,
      );
    },
    [handleClose, navigate],
  );

  const { handleProceed: runCheckout } = useCheckout({
    proceed,
    onProcessingStart: () => setIsProcessing(true),
    onProcessingStop: () => setIsProcessing(false),
    onResetAuthSuccess: () => setAuthSuccess(false),
    onRequireGuestAuth: () => setShowGuestModal(true),
    onRequireRegister: () => setShowRegisterModal(true),
  });

  const handleProceed = useCallback(
    (mode: number) =>
      runCheckout({
        mode,
        selectedAddressId,
        canPlaceOrder,
        hasOutOfStock,
        totalAmount,
        selectedProducts,
      }),
    [runCheckout, selectedAddressId, canPlaceOrder, hasOutOfStock, totalAmount, selectedProducts],
  );

  const warnNotVerified = () => {
    if (!authSuccess) {
      Swal.fire({
        icon: "warning",
        title: "User Not Verified",
        text: "You must complete verification to order.",
      });
    }
  };

  /* --------------------------- buy-now intent --------------------------- */

  // Select only the buy-now product and uncheck everything else.
  useEffect(() => {
    if (loading || cartItems.length === 0) return;

    const buyNowIntent = getBuyNowIntent();
    if (!buyNowIntent) {
      setPendingBuyNowIntent(null);
      return;
    }

    const productInCart = cartItems.some(
      (item: CartItemWithMapping) =>
        getCartItemMappingCode(item) === buyNowIntent.mappingCode,
    );

    if (!productInCart) {
      clearBuyNowIntent();
      setPendingBuyNowIntent(null);
      return;
    }

    setSelectedIds([buyNowIntent.mappingCode]);
    setPendingBuyNowIntent(buyNowIntent);
  }, [loading, cartItems, setSelectedIds]);

  // Auto-proceed once the buy-now product is selected and an address exists.
  useEffect(() => {
    if (
      !pendingBuyNowIntent ||
      loading ||
      isProcessing ||
      !selectedAddressId ||
      !selectedIds.includes(pendingBuyNowIntent.mappingCode) ||
      !canPlaceOrder
    ) {
      return;
    }

    clearBuyNowIntent();
    setPendingBuyNowIntent(null);
    handleProceed(PAYMENT_MODE.ONLINE);
  }, [
    pendingBuyNowIntent,
    loading,
    selectedIds,
    canPlaceOrder,
    selectedAddressId,
    isProcessing,
    handleProceed,
  ]);

  const itemCount = cartItems.length;

  return (
    <div
      className="cd-root"
      role="dialog"
      aria-modal="true"
      aria-label="Your bag"
    >
      <div className="cd-backdrop" onClick={handleClose} aria-hidden="true" />

      <aside className="cd-panel">
        {/* ------------------------------ header ------------------------------ */}
        <header className="cd-header">
          <h2 className="cd-title">
            Your Bag{itemCount ? ` (${itemCount})` : ""}
          </h2>
          <button
            type="button"
            className="cd-close"
            onClick={handleClose}
            aria-label="Close bag"
          >
            <X size={20} />
          </button>
        </header>

        {loading && itemCount === 0 ? (
          /* First read of the bag — nothing to show yet, so skeletons. */
          <div className="cd-body cd-body--plain">
            {[0, 1, 2].map((i) => (
              <div key={i} className="cd-skeleton" />
            ))}
          </div>
        ) : itemCount === 0 ? (
          /* ------------------------------ empty ------------------------------ */
          <div className="cd-body cd-empty">
            <div className="cd-empty__icon">
              <ShoppingBag size={26} />
            </div>
            <p className="cd-empty__title">Your bag is empty</p>
            <p className="cd-empty__text">
              Browse our collections and add a timepiece you love — it'll show up here.
            </p>
            <button
              type="button"
              className="cd-btn cd-btn--primary cd-empty__cta"
              onClick={() => {
                handleClose();
                navigate("/");
              }}
            >
              Start shopping
            </button>
          </div>
        ) : (
          <>
            {/* ---------------------------- scrollable ---------------------------- */}
            {/* data-lenis-prevent: the global Lenis smooth-scroll (hooks/useLenis.ts)
                would otherwise scroll the page behind the drawer instead of this. */}
            <div className="cd-body" data-lenis-prevent>
              {/* Re-reading the bag (item removed, qty changed) — show it's
                  loading without throwing the list away. */}
              {loading && (
                <div className="cd-refreshing">
                  <span className="cd-refreshing__spinner" />
                </div>
              )}

              <div className="cd-selectbar">
                <label className="cd-selectbar__label">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                  <span>
                    {selectedIds.length}/{itemCount} selected
                  </span>
                </label>
              </div>

              <section className="cd-items">
                {cartItems.map((product: any) => {
                  const mappingCode = getCartItemMappingCode(product);
                  return (
                    <CartDrawerItem
                      key={mappingCode}
                      product={product}
                      selected={selectedIds.includes(mappingCode)}
                      isQtyUpdating={!!pendingQtyUpdates[mappingCode]}
                      onSelect={handleSelectItem}
                      onQuantityChange={handleQuantityChange}
                      onRemoved={handleItemRemoved}
                      onOpenProduct={() => openProduct(product)}
                    />
                  );
                })}
              </section>

              <section className="cd-section">
                <AddressSummary
                  savedAddress={undefined}
                  onChange={() => {}}
                  onAddressChange={setSelectedAddressId}
                />
              </section>

              <section className="cd-section">
                <h3 className="cd-section__title">Payment</h3>
                <div className="cd-pay">
                  <PaymentChoice
                    icon={CreditCard}
                    title="Pay online"
                    note="Credit/Debit card, UPI, Wallet"
                    selected={effectivePaymentMode === PAYMENT_MODE.ONLINE}
                    disabled={!onlineAvailable}
                    disabledNote="Online payment is unavailable right now"
                    onSelect={() => setPaymentMode(PAYMENT_MODE.ONLINE)}
                  />
                  <PaymentChoice
                    icon={Banknote}
                    title="Cash on delivery"
                    note="Pay when your order arrives"
                    selected={effectivePaymentMode === PAYMENT_MODE.CASH}
                    disabled={!cashAvailable}
                    disabledNote={
                      isGuest
                        ? "Log in to pay with cash on delivery"
                        : "Cash on delivery is unavailable right now"
                    }
                    onSelect={() => setPaymentMode(PAYMENT_MODE.CASH)}
                  />
                </div>
              </section>
            </div>

            {/* ------------------------------ footer ------------------------------ */}
            <footer className="cd-footer">
              <div className="cd-totals">
                <div className="cd-totals__row">
                  <span>Items ({selectedProducts.length})</span>
                  <span>₹{totalMRP.toLocaleString("en-IN")}</span>
                </div>
                {totalDiscount > 0 && (
                  <div className="cd-totals__row cd-totals__row--save">
                    <span>Discount</span>
                    <span>− ₹{totalDiscount.toLocaleString("en-IN")}</span>
                  </div>
                )}
                <div className="cd-totals__row">
                  <span>Shipping</span>
                  <span>₹{shippingFee.toLocaleString("en-IN")}</span>
                </div>
              </div>

              <div className="cd-grand">
                <span>Total</span>
                <span className="cd-grand__value">
                  ₹{totalAmount.toLocaleString("en-IN")}
                </span>
              </div>

              <button
                type="button"
                className="cd-btn cd-btn--primary cd-pay-btn"
                disabled={isProcessing || !readyToPay}
                onClick={() => handleProceed(effectivePaymentMode)}
              >
                {isProcessing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Processing...
                  </>
                ) : effectivePaymentMode === PAYMENT_MODE.ONLINE ? (
                  `Pay ₹${totalAmount.toLocaleString("en-IN")}`
                ) : (
                  `Place order · ₹${totalAmount.toLocaleString("en-IN")}`
                )}
              </button>

              {blockingReason && <p className="cd-note">{blockingReason}</p>}
            </footer>
          </>
        )}

        <RegisterModal
          show={showRegisterModal}
          onHide={() => {
            setShowRegisterModal(false);
            warnNotVerified();
          }}
          onSuccess={() => setAuthSuccess(true)}
        />

        <RegisterModal
          show={showGuestModal}
          onHide={() => {
            setShowGuestModal(false);
            warnNotVerified();
          }}
          isGuest
          onSuccess={() => setAuthSuccess(true)}
        />
      </aside>
    </div>
  );
};

/** One selectable payment method card. Disabled methods stay visible and say why. */
const PaymentChoice: React.FC<{
  icon: typeof CreditCard;
  title: string;
  note: string;
  selected: boolean;
  disabled: boolean;
  disabledNote: string;
  onSelect: () => void;
}> = ({ icon: Icon, title, note, selected, disabled, disabledNote, onSelect }) => (
  <button
    type="button"
    className={`cd-pay__choice${selected ? " is-selected" : ""}`}
    onClick={onSelect}
    disabled={disabled}
  >
    <span className="cd-pay__icon">
      <Icon size={16} />
    </span>
    <span className="cd-pay__text">
      <span className="cd-pay__title">{title}</span>
      <span className="cd-pay__note">{disabled ? disabledNote : note}</span>
    </span>
    <span className="cd-pay__radio" />
  </button>
);

export default CartDrawer;
